import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileText, Plus, Trash2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const statusColor = (s) => ({
  Created: 'bg-blue-100 text-blue-700', Released: 'bg-sky-100 text-sky-700',
  Confirmed: 'bg-amber-100 text-amber-700', 'Technically Complete': 'bg-indigo-100 text-indigo-700',
  Closed: 'bg-green-100 text-green-700',
}[s] || 'bg-gray-100');
const NEW_OP = { operation_number: '', description: '', work_center: '', planned_work_hours: '', activity_rate: '' };
const NEW_COMP = { material: '', description: '', quantity: '', unit: 'EA', plant: '1000', storage_location: '0001', unit_price: '' };
const EMPTY = { order_type: 'Corrective', description: '', equipment: '', functional_location: '', priority: 'Medium', maintenance_plant: '1000', main_work_center: '', operations: [{ ...NEW_OP }], components: [] };

const WorkOrders = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('maintenance_order');
  const [rows, setRows] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState(null);
  const [conf, setConf] = useState({ operation_number: '', work_hours: '', confirmation_text: '', final: false });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/pm/orders`, { withCredentials: true }); setRows(res.data); }
    catch (e) { toast.error('Failed to load work orders'); }
    finally { setLoading(false); }
  }, []);
  const loadEquipment = useCallback(async () => {
    try { const res = await axios.get(`${API}/pm/equipment`, { withCredentials: true }); setEquipment(res.data); }
    catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); loadEquipment(); }, [load, loadEquipment]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setOp = (i, k, v) => setForm((p) => { const a = [...p.operations]; a[i] = { ...a[i], [k]: v }; return { ...p, operations: a }; });
  const addOp = () => setForm((p) => ({ ...p, operations: [...p.operations, { ...NEW_OP }] }));
  const removeOp = (i) => setForm((p) => ({ ...p, operations: p.operations.filter((_, x) => x !== i) }));
  const setComp = (i, k, v) => setForm((p) => { const a = [...p.components]; a[i] = { ...a[i], [k]: v }; return { ...p, components: a }; });
  const addComp = () => setForm((p) => ({ ...p, components: [...p.components, { ...NEW_COMP }] }));
  const removeComp = (i) => setForm((p) => ({ ...p, components: p.components.filter((_, x) => x !== i) }));

  const create = async () => {
    setSaving(true);
    try {
      const payload = {
        order_type: form.order_type, description: form.description, priority: form.priority,
        equipment: form.equipment || null, functional_location: form.functional_location || null,
        maintenance_plant: form.maintenance_plant || null, main_work_center: form.main_work_center || null,
        operations: form.operations.filter((o) => o.operation_number && o.description).map((o) => ({
          operation_number: o.operation_number, description: o.description, work_center: o.work_center || null,
          planned_work_hours: parseFloat(o.planned_work_hours) || 0, activity_rate: parseFloat(o.activity_rate) || 0,
        })),
        components: form.components.filter((c) => c.material).map((c) => ({
          material: c.material, description: c.description || null, quantity: parseFloat(c.quantity) || 0,
          unit: c.unit || 'EA', plant: c.plant || '1000', storage_location: c.storage_location || '0001',
          unit_price: parseFloat(c.unit_price) || 0,
        })),
      };
      await axios.post(`${API}/pm/orders`, payload, { withCredentials: true });
      toast.success('Work order created'); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create order'); }
    finally { setSaving(false); }
  };

  const openOrder = async (num) => {
    try {
      const res = await axios.get(`${API}/pm/orders/${num}`, { withCredentials: true });
      setOrder(res.data);
      setConf({ operation_number: res.data.operations?.[0]?.operation_number || '', work_hours: '', confirmation_text: '', final: false });
    } catch (e) { toast.error('Failed to open order'); }
  };

  const action = async (path, body) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/pm/orders/${order.order_number}/${path}`, body || {}, { withCredentials: true });
      setOrder(res.data); toast.success('Done'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const submitConfirm = async () => {
    if (!conf.work_hours || parseFloat(conf.work_hours) <= 0) return toast.error('Enter work hours');
    await action('confirm', {
      operation_number: conf.operation_number || null, work_hours: parseFloat(conf.work_hours),
      confirmation_text: conf.confirmation_text || null, final: conf.final,
    });
    setConf((p) => ({ ...p, work_hours: '', confirmation_text: '' }));
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="work-orders-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-sky-100 text-sky-700"><FileText className="h-5 w-5" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Maintenance Work Orders</h1>
              <p className="text-gray-600 text-sm mt-0.5">Plan, release, confirm and settle maintenance work.</p>
            </div>
          </div>
          {writable && <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-order-btn"><Plus className="h-4 w-4 mr-2" />New Work Order</Button>}
        </div>

        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Order #</TableHead><TableHead>Type</TableHead><TableHead>Description</TableHead>
              <TableHead>Equipment</TableHead><TableHead className="text-right">Planned</TableHead>
              <TableHead className="text-right">Actual</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">Loading...</TableCell></TableRow>
                : rows.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">No work orders yet.</TableCell></TableRow>
                : rows.map((r) => (
                  <TableRow key={r.order_number} data-testid={`order-row-${r.order_number}`}>
                    <TableCell className="font-medium">{r.order_number}</TableCell>
                    <TableCell>{r.order_type}</TableCell>
                    <TableCell className="max-w-xs truncate">{r.description}</TableCell>
                    <TableCell>{r.equipment || '—'}</TableCell>
                    <TableCell className="text-right">${r.planned_cost}</TableCell>
                    <TableCell className="text-right">${r.actual_cost}</TableCell>
                    <TableCell><span className={`px-2 py-1 rounded text-xs font-medium ${statusColor(r.status)}`}>{r.status}</span></TableCell>
                    <TableCell><Button size="sm" variant="outline" onClick={() => openOrder(r.order_number)} data-testid={`open-order-${r.order_number}`}>Open</Button></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Work Order</DialogTitle>
            <DialogDescription>Add operations (labor) and components (materials).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-4 py-2">
            <div>
              <Label>Order Type</Label>
              <Select value={form.order_type} onValueChange={(v) => set('order_type', v)}>
                <SelectTrigger data-testid="order-order-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Breakdown">Breakdown</SelectItem>
                  <SelectItem value="Corrective">Corrective</SelectItem>
                  <SelectItem value="Preventive">Preventive</SelectItem>
                  <SelectItem value="Calibration">Calibration</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set('priority', v)}>
                <SelectTrigger data-testid="order-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Very High">Very High</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Equipment</Label>
              <Select value={form.equipment || 'none'} onValueChange={(v) => set('equipment', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="order-equipment"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {equipment.map((eq) => <SelectItem key={eq.equipment_number} value={eq.equipment_number}>{eq.equipment_number} — {eq.description}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-3"><Label>Description *</Label><Input data-testid="order-description" value={form.description} onChange={(e) => set('description', e.target.value)} /></div>
            <div><Label>Maintenance Plant</Label><Input data-testid="order-plant" value={form.maintenance_plant} onChange={(e) => set('maintenance_plant', e.target.value)} /></div>
            <div><Label>Main Work Center</Label><Input data-testid="order-workcenter" value={form.main_work_center} onChange={(e) => set('main_work_center', e.target.value)} /></div>
          </div>

          {/* Operations */}
          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900">Operations (labor)</h4>
              <Button size="sm" variant="outline" onClick={addOp} data-testid="add-op-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
            </div>
            <div className="space-y-2">
              {form.operations.map((o, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`op-row-${i}`}>
                  <Input className="col-span-2" placeholder="Op #" data-testid={`op-number-${i}`} value={o.operation_number} onChange={(e) => setOp(i, 'operation_number', e.target.value)} />
                  <Input className="col-span-4" placeholder="Description" data-testid={`op-desc-${i}`} value={o.description} onChange={(e) => setOp(i, 'description', e.target.value)} />
                  <Input className="col-span-2" placeholder="Work Ctr" value={o.work_center} onChange={(e) => setOp(i, 'work_center', e.target.value)} />
                  <Input className="col-span-1" placeholder="Hrs" type="number" data-testid={`op-hours-${i}`} value={o.planned_work_hours} onChange={(e) => setOp(i, 'planned_work_hours', e.target.value)} />
                  <Input className="col-span-2" placeholder="Rate/h" type="number" data-testid={`op-rate-${i}`} value={o.activity_rate} onChange={(e) => setOp(i, 'activity_rate', e.target.value)} />
                  <Button size="sm" variant="ghost" className="col-span-1" onClick={() => removeOp(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
            </div>
          </div>

          {/* Components */}
          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900">Components (materials)</h4>
              <Button size="sm" variant="outline" onClick={addComp} data-testid="add-comp-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
            </div>
            <div className="space-y-2">
              {form.components.length === 0 && <p className="text-xs text-gray-400">No components. Add spare parts to be issued from stock.</p>}
              {form.components.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`comp-row-${i}`}>
                  <Input className="col-span-3" placeholder="Material" data-testid={`comp-material-${i}`} value={c.material} onChange={(e) => setComp(i, 'material', e.target.value)} />
                  <Input className="col-span-3" placeholder="Description" value={c.description} onChange={(e) => setComp(i, 'description', e.target.value)} />
                  <Input className="col-span-2" placeholder="Qty" type="number" data-testid={`comp-qty-${i}`} value={c.quantity} onChange={(e) => setComp(i, 'quantity', e.target.value)} />
                  <Input className="col-span-1" placeholder="SLoc" value={c.storage_location} onChange={(e) => setComp(i, 'storage_location', e.target.value)} />
                  <Input className="col-span-2" placeholder="Price" type="number" data-testid={`comp-price-${i}`} value={c.unit_price} onChange={(e) => setComp(i, 'unit_price', e.target.value)} />
                  <Button size="sm" variant="ghost" className="col-span-1" onClick={() => removeComp(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving || !form.description} data-testid="save-order-btn">{saving ? 'Creating...' : 'Create Order'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / lifecycle dialog */}
      <Dialog open={!!order} onOpenChange={(o) => !o && setOrder(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          {order && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">{order.order_number}
                  <span className={`px-2 py-1 rounded text-xs font-medium ${statusColor(order.status)}`} data-testid="order-detail-status">{order.status}</span>
                </DialogTitle>
                <DialogDescription>{order.order_type} · {order.description} {order.equipment ? `· ${order.equipment}` : ''}</DialogDescription>
              </DialogHeader>

              {/* Cost summary */}
              <div className="grid grid-cols-4 gap-3">
                {[
                  { l: 'Planned Cost', v: `$${order.planned_cost}` },
                  { l: 'Actual Cost', v: `$${order.actual_cost}`, testid: 'order-actual-cost' },
                  { l: 'Labor (act)', v: `$${order.actual_labor_cost}` },
                  { l: 'Material (act)', v: `$${order.actual_material_cost}` },
                ].map((k) => (
                  <div key={k.l} className="rounded-lg bg-gray-50 border p-2">
                    <div className="text-xs text-gray-500">{k.l}</div>
                    <div className="text-lg font-bold text-gray-900" data-testid={k.testid}>{k.v}</div>
                  </div>
                ))}
              </div>

              {/* Operations */}
              {order.operations?.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Op</TableHead><TableHead>Description</TableHead><TableHead>Work Ctr</TableHead>
                      <TableHead className="text-right">Plan h</TableHead><TableHead className="text-right">Actual h</TableHead><TableHead>Status</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {order.operations.map((o, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{o.operation_number}</TableCell>
                          <TableCell>{o.description}</TableCell><TableCell>{o.work_center || '—'}</TableCell>
                          <TableCell className="text-right">{o.planned_work_hours}</TableCell>
                          <TableCell className="text-right">{o.actual_work_hours}</TableCell>
                          <TableCell>{o.status === 'Confirmed' ? <span className="text-green-600 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" />Confirmed</span> : 'Open'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Components */}
              {order.components?.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Material</TableHead><TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Price</TableHead><TableHead>SLoc</TableHead><TableHead>Issued</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {order.components.map((c, i) => (
                        <TableRow key={i} data-testid={`order-comp-${i}`}>
                          <TableCell className="font-medium">{c.material}</TableCell>
                          <TableCell className="text-right">{c.quantity} {c.unit}</TableCell>
                          <TableCell className="text-right">${c.unit_price}</TableCell>
                          <TableCell>{c.storage_location}</TableCell>
                          <TableCell>{c.withdrawn ? <span className="text-green-600">Yes</span> : 'No'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Confirmations log */}
              {order.confirmations?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm mb-1">Confirmations</h4>
                  <div className="space-y-1 max-h-32 overflow-auto">
                    {order.confirmations.map((c, i) => (
                      <div key={i} className="text-xs bg-gray-50 rounded p-2 flex justify-between" data-testid={`confirmation-${i}`}>
                        <span>{c.confirmation_number} · Op {c.operation_number || '—'} · {c.work_hours}h · ${c.labor_cost}{c.final ? ' · final' : ''}</span>
                        <span className="text-gray-400">{c.posted_by}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Lifecycle actions */}
              {writable && order.status !== 'Closed' && (
                <Card className="p-4 border-sky-100 space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {order.status === 'Created' && <Button size="sm" onClick={() => action('release')} disabled={busy} data-testid="release-order-btn">Release</Button>}
                    {order.components?.some((c) => !c.withdrawn) && ['Released', 'Confirmed'].includes(order.status) &&
                      <Button size="sm" variant="outline" onClick={() => action('issue-components')} disabled={busy} data-testid="issue-components-btn">Issue Components</Button>}
                    {['Released', 'Confirmed'].includes(order.status) &&
                      <Button size="sm" variant="outline" onClick={() => action('teco')} disabled={busy} data-testid="teco-order-btn">Technically Complete</Button>}
                    {order.status === 'Technically Complete' &&
                      <Button size="sm" onClick={() => action('close')} disabled={busy} data-testid="close-order-btn">Close Order</Button>}
                  </div>

                  {['Released', 'Confirmed'].includes(order.status) && order.operations?.length > 0 && (
                    <div className="border-t pt-3">
                      <h4 className="font-semibold text-gray-900 mb-2">Confirm Operation</h4>
                      <div className="grid grid-cols-4 gap-3 items-end">
                        <div>
                          <Label>Operation</Label>
                          <Select value={conf.operation_number} onValueChange={(v) => setConf((p) => ({ ...p, operation_number: v }))}>
                            <SelectTrigger data-testid="conf-operation"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {order.operations.map((o) => <SelectItem key={o.operation_number} value={o.operation_number}>{o.operation_number} — {o.description}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div><Label>Actual Hours</Label><Input type="number" data-testid="conf-hours" value={conf.work_hours} onChange={(e) => setConf((p) => ({ ...p, work_hours: e.target.value }))} /></div>
                        <div className="col-span-2"><Label>Text</Label><Input data-testid="conf-text" value={conf.confirmation_text} onChange={(e) => setConf((p) => ({ ...p, confirmation_text: e.target.value }))} /></div>
                      </div>
                      <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={conf.final} data-testid="conf-final" onChange={(e) => setConf((p) => ({ ...p, final: e.target.checked }))} />
                        Final confirmation
                      </label>
                      <Button className="mt-2" size="sm" onClick={submitConfirm} disabled={busy} data-testid="submit-confirm-btn">Post Confirmation</Button>
                    </div>
                  )}
                </Card>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default WorkOrders;
